import { ofType } from 'redux-observable';
import { mergeMap, switchMap } from 'rxjs/operators';

import { AUTH_MFA_RESEND_ENDPOINT } from '@perpay-web/authentication/constants/urls';
import { ERROR_TOO_MANY_REQUESTS } from '@perpay-web/constants/httpStatusCodes';
import {
    resendProfileMFACode as resendProfileMFACodeAction,
    resendProfileMFACodeError,
    resendProfileMFACodeSuccess,
} from '@perpay-web/fintech/actions/authentication';
import {
    sendMFACode as sendMFACodeAction,
    sendMFACodeError,
    sendMFACodeSuccess,
} from '@perpay-web/fintech/actions/entities/sendMFACode';
import {
    validateMFACode as validateMFACodeAction,
    validateMFACodeError,
    validateMFACodeSuccess,
} from '@perpay-web/fintech/actions/entities/validateMFACodes';
import {
    SEND_MFA_CODE_ENDPOINT,
    VALIDATE_MFA_CODE_ENDPOINT,
} from '@perpay-web/fintech/constants/urls';
import {
    attemptValidateMFAErrorHandling,
    resendMFACodeContextSuccess,
    resendMFAErrorHandling,
    sendMFACodeContextSuccess,
    sendMFAErrorHandling,
    throttleMFAErrorHandling,
    validateMFACodeContextSuccess,
} from '@perpay-web/fintech/hooks/useProfileMFAContext';
import { paths } from '@perpay-web/fintech/props/appPaths';
import { handleErrorMessageWithFallbackWithStatus } from '@perpay-web/observable/operators/handleErrorMessageWithFallback';
import { replace } from '@perpay-web/services/router';

export const sendMFACode = (action$, state$, { post }) =>
    action$.pipe(
        ofType(sendMFACodeAction().type),
        switchMap((action) => post(SEND_MFA_CODE_ENDPOINT, action.payload)),
        mergeMap((results) => {
            localStorage.setItem('otpUuid', results.response.otpUuid);
            return [
                sendMFACodeSuccess(results.response),
                sendMFACodeContextSuccess(results.response.otpUuid),
            ];
        }),
        handleErrorMessageWithFallbackWithStatus((error) => {
            const mfaCodeSendError = {};
            if (error.status === ERROR_TOO_MANY_REQUESTS) {
                mfaCodeSendError.throttle = error;
                return [
                    sendMFACodeError(error),
                    throttleMFAErrorHandling(mfaCodeSendError.throttle),
                ];
            }

            mfaCodeSendError.send = error;
            return [
                sendMFACodeError(error),
                sendMFAErrorHandling(mfaCodeSendError.send),
            ];
        }),
    );

export const validateMFACode = (action$, state$, { post }) =>
    action$.pipe(
        ofType(validateMFACodeAction().type),
        switchMap((action) => {
            if (action.payload.otp_uuid) {
                return post(VALIDATE_MFA_CODE_ENDPOINT, action.payload);
            }

            const otpUuidFromLocalStorage = localStorage.getItem('otpUuid');

            if (otpUuidFromLocalStorage) {
                const payload = {
                    otp_uuid: otpUuidFromLocalStorage,
                    code: action.payload.code,
                };
                return post(VALIDATE_MFA_CODE_ENDPOINT, payload);
            }

            replace(paths.profile.path);
            return [];
        }),
        mergeMap((results) => {
            localStorage.removeItem('otpUuid');
            return [
                validateMFACodeSuccess(results.response),
                validateMFACodeContextSuccess(),
            ];
        }),
        handleErrorMessageWithFallbackWithStatus((error) => {
            const mfaCodeValidationError = {};
            if (error.status === ERROR_TOO_MANY_REQUESTS) {
                mfaCodeValidationError.throttle = error;
                return [
                    validateMFACodeError(error),
                    throttleMFAErrorHandling(mfaCodeValidationError.throttle),
                ];
            }

            mfaCodeValidationError.attempt = error;
            return [
                validateMFACodeError(error),
                attemptValidateMFAErrorHandling(mfaCodeValidationError.attempt),
            ];
        }),
    );

export const resendMFACode = (action$, state$, { post }) =>
    action$.pipe(
        ofType(resendProfileMFACodeAction().type),
        switchMap((action) => post(AUTH_MFA_RESEND_ENDPOINT, action.payload)),
        mergeMap((results) => {
            localStorage.setItem('otpUuid', results.response.otpUuid);
            return [
                resendProfileMFACodeSuccess(results.response),
                resendMFACodeContextSuccess(results.response.otpUuid),
            ];
        }),
        handleErrorMessageWithFallbackWithStatus((error) => {
            const mfaCodeResendError = {};
            if (error.status === ERROR_TOO_MANY_REQUESTS) {
                mfaCodeResendError.throttle = error;
                return [
                    resendProfileMFACodeError(error),
                    throttleMFAErrorHandling(mfaCodeResendError.throttle),
                ];
            }

            mfaCodeResendError.resend = error;
            return [
                resendProfileMFACodeError(error),
                resendMFAErrorHandling(mfaCodeResendError.resend),
            ];
        }),
    );
